import { LoadingButton } from '@mui/lab';
import { IconButton, Stack, SvgIcon, useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import React from 'react';

import Iconify from '../iconify';
import { CustomDialogProps } from './types';

// ----------------------------------------------------------------------

export default function CustomDialog({
  icon,
  title,
  content,
  open,
  confirmText,
  cancelText,
  onClose,
  onConfirm,
  ...other
}: CustomDialogProps) {
  const Icon = React.lazy(() => import(`src/assets/icons/all/${icon}.svg?react`));
  const theme = useTheme();

  return (
    <Dialog fullWidth maxWidth="xs" open={open} onClose={onClose} {...other}>
      <Stack sx={{ p: 1 }}>
        <DialogTitle sx={{ typography: 'header-sm' }}>
          <Stack alignItems="center" spacing={2} width="100%">
            <Stack alignItems="end" width="100%">
              <IconButton onClick={onClose}>
                <Iconify icon="ph:x" />
              </IconButton>
            </Stack>
            <React.Suspense fallback={<div>Loading...</div>}>
              <SvgIcon
                component={Icon as React.ElementType}
                sx={{
                  width: 64,
                  height: 64,
                  color: 'brand.400',
                }}
              />
            </React.Suspense>
            {title}
          </Stack>
        </DialogTitle>

        {content && (
          <DialogContent sx={{ typography: 'body-sm' }}>
            <Stack alignItems="center" width="100%">
              {content}
            </Stack>
          </DialogContent>
        )}

        <DialogActions sx={{ alignItems: 'center', justifyContent: 'center', typography: 'body-md' }}>
          <Stack spacing={1} width="100%">
            <LoadingButton
              variant="contained"
              onClick={onConfirm}
              sx={{
                borderRadius: 0,
                textTransform: 'none',
                color: theme.palette.common.white,
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              {confirmText}
            </LoadingButton>

            {cancelText && (
              <Button
                variant="text"
                onClick={onClose}
                sx={{
                  borderRadius: 0,
                  textTransform: 'none',
                  color: theme.palette.primary.main,
                }}
              >
                {cancelText}
              </Button>
            )}
          </Stack>
        </DialogActions>
      </Stack>
    </Dialog>
  );
}
